import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import {
    ALL_BRANDS_QUERY,
    type BrandListQuery,
} from '@/api/handlers/resources/brands/requests';
import type { GetBrandListResponse } from '@/api/handlers/resources/brands/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BrandQueryKeys } from './brandQueryKeys';

/**
 * Reads `GET /brands/list` (server-paginated, BOS-81). Consumers that just need
 * the whole brand set (product / purchase pickers, label lookups) call this
 * with no query → it defaults to {@link ALL_BRANDS_QUERY} (the largest page the
 * API allows). The Brands management table passes an explicit page/pageSize/
 * search instead.
 */
export const useBrandListQuery = (
    query: BrandListQuery = ALL_BRANDS_QUERY,
    enabled = true,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetBrandListResponse>>({
        queryKey: BrandQueryKeys.listPage(query),
        queryFn: () => apiClient.brands.getBrandList(query),
        enabled,
        staleTime: 60_000,
        // Keep the current page on screen while the next one loads so paging
        // doesn't flash the skeleton.
        placeholderData: keepPreviousData,
    });
};
